import type {
  LlmModel,
  LlmTaskWithPool,
} from 'src/secret-menu/llm-dispatcher/hooks/useLlmCatalogue';

export const IN_POOL = 'Wired to this task';
export const AVAILABLE = 'Other models';
export const UNSUPPORTED = 'Wrong modality for this task';

const ORDER = [IN_POOL, AVAILABLE, UNSUPPORTED];

export const qualified = (model: LlmModel) => `${model.providerKey}/${model.name}`;

/**
 * The dispatcher only ranks a pool member that declares the task's modality, so a model without it
 * would be dropped at dispatch and fail the run. Absent a task it cannot be judged, so nothing is
 * blocked.
 */
const supportsTask = (model: LlmModel, task: LlmTaskWithPool | undefined) =>
  !task || model.modalities.some(m => m.modality === task.modality);

type CandidateOption = { value: string; label: string; disabled: boolean };

type CandidateGroup = { group: string; items: CandidateOption[] };

/**
 * Every model in the catalogue as pickable candidates, split by how they stand to this task.
 *
 * The options carry no `group` key of their own: Mantine reads any option object with one as a
 * group and then walks its `items`, so a stray key crashes the dropdown.
 */
export const groupCandidates = (
  models: LlmModel[],
  task: LlmTaskWithPool | undefined,
): CandidateGroup[] => {
  const pooledIds = new Set(task?.pool.map(entry => entry.model.id) ?? []);

  const groupFor = (model: LlmModel) => {
    if (!supportsTask(model, task)) return UNSUPPORTED;
    return pooledIds.has(model.id) ? IN_POOL : AVAILABLE;
  };

  return ORDER.map(group => ({
    group,
    items: models
      .filter(model => groupFor(model) === group)
      .map(model => ({
        value: model.id,
        label: qualified(model),
        disabled: !supportsTask(model, task),
      })),
  })).filter(({ items }) => items.length > 0);
};
